/**
 * Pure model for the Human Call tab's guided **Call Script** flow.
 *
 * Given an active {@link Script}, the workflow's output schema groups, and the
 * live output/input data + disposition state, this builds an ordered list of
 * {@link ScriptUnit}s:
 *   - top-level questions produce a single unit;
 *   - entity-scoped questions (e.g. `drug`, `procedure`) **fan out** into one
 *     unit per billable code found in the case input (J-code / CPT / site).
 *
 * Each unit carries fully {@link ResolvedField}s — the concrete output key
 * (top-level id or `entity[i].relative.path`, matching `parseArrayItemPath`),
 * the schema {@link FormField} def (handed straight to `ScalarFieldEditable`),
 * the current value, and a live {@link FieldStatus}.
 *
 * This module is PURE (no React, no I/O) so it can be unit-tested in isolation.
 */
import type { Script, ScriptQuestion } from '@compass/client/types/script';

import type { FieldGroup, FormField } from '@/types/formSchema';

import type { FieldStatus } from './fieldStatus';
import {
  computeStatus,
  findEntityField,
  findTopLevelFieldByPath,
  readRelativePath,
  resolveTemplateField,
} from './fieldStatus';
import type { StatusCtx, TreeLeaf } from './fieldTree';
import { ENTITY_INPUT_KEY, walkTemplateToLeaves } from './fieldTree';

export interface ResolvedField {
  /** Concrete output key: top-level id OR "entity[i].relative.path". */
  key: string;
  /** Schema field def (label/type/options) — fed straight to ScalarFieldEditable. */
  field: FormField;
  /** Current value read from the output data at `key`. */
  value: unknown;
  status: FieldStatus;
  /** True when the disposition engine lists this field as required for the scenario. */
  required: boolean;
  /**
   * Nearest enclosing nested-object label ("Coverage") — the section this field renders
   * under inside its unit. Undefined for a direct entity/top-level scalar. Set when a
   * ref lands in (or expands from) a nested object; drives the section sub-headers in
   * ScriptStep. Render-only — does not affect required/done/currentUnitId logic.
   */
  section?: string;
}

export interface ScriptUnit {
  /** Unique id for the unit; `questionId` for top-level, `questionId#index` when fanned out. */
  id: string;
  questionId: string;
  /** Optional section heading shown on the accordion (falls back to the prompt). */
  section: string;
  prompt: string;
  helpText: string;
  entityKey: string | null;
  /** Present only when the question fanned out across entity instances. */
  instance?: { index: number; code: string; name?: string };
  fields: ResolvedField[];
  /** True when no field in the unit has status `missing`. */
  done: boolean;
  /**
   * True when the authoring question is flagged `always_show` (intro/pinned). Such
   * units render at the TOP of the flow, with ALL their fields, always visible and
   * open by default — bypassing the required-only section/field filters. Defaults
   * to false for ordinary questions.
   */
  alwaysShow: boolean;
}

export interface ScriptModel {
  units: ScriptUnit[];
  /**
   * The id of the first unit that still has a `missing` field (not heard, not
   * captured). Used by ScriptFlow to auto-advance the accordion to the next
   * unanswered section. Null when everything is filled or heard.
   */
  currentUnitId: string | null;
  /**
   * REQUIRED fields that NO script question covers — surfaced as a catch-all "Other
   * required fields" section so they can still be captured during the call. Sourced from
   * the required set, not the missing set, so a row stays put once it is filled instead of
   * disappearing under the rep (BV-1575).
   */
  otherFields: ResolvedField[];
}

/**
 * Sort questions by `order` (ascending), stable on input order for ties.
 *
 * A missing `order` coalesces to 0 — NOT a large sentinel. `order` is a proto3
 * int32 and the HTTP API marshals with `EmitUnpopulated:false`, so the first
 * question (`order:0`) arrives with `order` omitted/undefined; an absent proto3
 * int32 means its default value 0, so it must sort FIRST. Treating undefined as
 * "last" sent the top section to the bottom (BI-1217).
 */
function sortedQuestions(questions: ScriptQuestion[]): ScriptQuestion[] {
  return questions
    .map((q, i) => ({ q, i }))
    .sort((a, b) => {
      const ao = a.q.order ?? 0;
      const bo = b.q.order ?? 0;
      return ao === bo ? a.i - b.i : ao - bo;
    })
    .map(({ q }) => q);
}

/** Build a StatusCtx (for walkTemplateToLeaves) from a BuildContext. */
function toStatusCtx(ctx: BuildContext): StatusCtx {
  return {
    missing: ctx.missingFieldSet,
    required: ctx.requiredFieldSet,
    heard: ctx.heardFieldSet,
    edited: ctx.sessionEditedKeys,
  };
}

/**
 * Drop later fields whose key already appeared (first occurrence wins, order preserved).
 * A section ref that expands to leaves can overlap an explicit leaf ref for the same key
 * (both selectable in the admin picker); this keeps each concrete key a single row.
 */
function dedupeByKey(fields: ResolvedField[]): ResolvedField[] {
  const seen = new Set<string>();
  const out: ResolvedField[] = [];
  for (const f of fields) {
    if (seen.has(f.key)) continue;
    seen.add(f.key);
    out.push(f);
  }
  return out;
}

/** Map an expanded TreeLeaf to a ResolvedField (the unit already scopes the instance). */
function leafToResolvedField(leaf: TreeLeaf): ResolvedField {
  return {
    key: leaf.key,
    field: leaf.field,
    value: leaf.value,
    status: leaf.status,
    required: leaf.required,
    section: leaf.section,
  };
}

/**
 * The section label for a (non-expanded) leaf ref: its parent object's label when the
 * ref is a nested path (`coverage.copay` → "Coverage"), else undefined for a direct leaf.
 */
function sectionForPath(
  path: string,
  resolveDef: (p: string) => FormField | undefined,
): string | undefined {
  const idx = path.lastIndexOf('.');
  if (idx < 0) return undefined;
  const parent = resolveDef(path.slice(0, idx));
  return parent && (parent.type === 'object' || parent.type === 'array') ? parent.label : undefined;
}

/**
 * True when a ref's def is a container that actually has a nested template to expand.
 * A `fields`-less array (a repeated scalar/enum, e.g. `notification_methods`) is NOT
 * expandable — it resolves to a single multi-value leaf (matching `walkTemplateToLeaves`).
 */
function isExpandable(def: FormField): boolean {
  return (def.type === 'object' || def.type === 'array') && (def.fields?.length ?? 0) > 0;
}

/** Resolve the field_refs of a top-level (non entity-scoped) question. */
function resolveTopLevelFields(question: ScriptQuestion, ctx: BuildContext): ResolvedField[] {
  const statusCtx = toStatusCtx(ctx);
  const resolveDef = (p: string) => findTopLevelFieldByPath(ctx.outputSchemaGroups, p);
  const fields: ResolvedField[] = [];
  for (const ref of question.field_refs ?? []) {
    const fieldId = ref.field_id;
    if (!fieldId) continue;
    const def = resolveDef(fieldId);
    if (!def) continue;
    const value = readRelativePath(ctx.outputData, fieldId);
    // A ref that lands on a nested object/array expands into its leaf descendants;
    // a scalar ref resolves to a single field.
    if (isExpandable(def)) {
      fields.push(...walkTemplateToLeaves(def, fieldId, value, statusCtx).map(leafToResolvedField));
    } else {
      fields.push({
        key: fieldId,
        field: def,
        value,
        status: computeStatus(
          fieldId,
          value,
          ctx.missingFieldSet,
          ctx.heardFieldSet,
          ctx.sessionEditedKeys,
        ),
        required: ctx.requiredFieldSet.has(fieldId),
        section: sectionForPath(fieldId, resolveDef),
      });
    }
  }
  return dedupeByKey(fields);
}

/** Resolve the field_refs of an entity-scoped question for one instance index. */
function resolveEntityFields(
  question: ScriptQuestion,
  entityKey: string,
  index: number,
  entityTemplate: FormField | undefined,
  outputElement: unknown,
  ctx: BuildContext,
): ResolvedField[] {
  const statusCtx = toStatusCtx(ctx);
  const resolveDef = (p: string) => resolveTemplateField(entityTemplate, p);
  const fields: ResolvedField[] = [];
  for (const ref of question.field_refs ?? []) {
    const relative = ref.field_id;
    if (!relative) continue;
    const def = resolveDef(relative);
    if (!def) continue;
    const key = `${entityKey}[${index}].${relative}`;
    const value = readRelativePath(outputElement, relative);
    if (isExpandable(def)) {
      fields.push(...walkTemplateToLeaves(def, key, value, statusCtx).map(leafToResolvedField));
    } else {
      fields.push({
        key,
        field: def,
        value,
        status: computeStatus(
          key,
          value,
          ctx.missingFieldSet,
          ctx.heardFieldSet,
          ctx.sessionEditedKeys,
        ),
        required: ctx.requiredFieldSet.has(key),
        section: sectionForPath(relative, resolveDef),
      });
    }
  }
  return dedupeByKey(fields);
}

function unitDone(fields: ResolvedField[]): boolean {
  return !fields.some(f => f.status === 'missing');
}

/** Display state for a unit's header badge. */
export type UnitState = 'pending' | 'done' | 'to-collect' | 'optional';

/**
 * A field is "engaged" once it carries a value, is already on file, or was heard on
 * the recording — i.e. the rep has captured (or acknowledged) it. An empty `missing`
 * or `optional` field is NOT engaged.
 */
function isFieldEngaged(field: ResolvedField): boolean {
  return field.status === 'captured' || field.status === 'prefilled' || field.status === 'heard';
}

/**
 * Classify a unit for its header badge:
 *   - `pending`    — has required fields still missing (→ "N pending").
 *   - `done`       — has a required field that is filled/on-file/heard, and none are
 *                    still missing (→ "Done ✓").
 *   - `to-collect` — has required fields, none missing, but NOTHING engaged yet. This is
 *                    the always-collect case: the engine's `OPTIONAL_FIELDS` are merged
 *                    into `required` (so they surface in the script) but never into
 *                    `missing` (so they never block completion). An all-empty section of
 *                    them must NOT read as "done" — a rep scanning for green checkmarks
 *                    would skip the very fields the feature exists to capture (BI-1217).
 *   - `optional`   — has NO required fields for this scenario (→ "Optional").
 *
 * This is why a unit whose fields the engine doesn't require shows "Optional", not
 * "Done": there was nothing required to capture, so completion is undefined — the
 * old `done` flag (no-missing) was vacuously true and read as misleadingly complete.
 * `to-collect` is the analogous fix for required-but-never-missing always-collect fields.
 *
 * `heard` counts fields the agent acknowledged on-recording but hasn't committed yet.
 * These are NOT pending — they don't block currentUnitId advancement — but they DO count
 * as engaged, so a heard required field makes a unit "done", not "to-collect".
 */
export function unitProgress(unit: ScriptUnit): {
  pending: number;
  heard: number;
  state: UnitState;
} {
  const pending = unit.fields.filter(f => f.status === 'missing').length;
  const heard = unit.fields.filter(f => f.status === 'heard').length;
  if (pending > 0) return { pending, heard, state: 'pending' };
  const hasRequired = unit.fields.some(f => f.required);
  if (!hasRequired) return { pending: 0, heard, state: 'optional' };
  // No required field is missing. "Done" only when a required field is actually engaged
  // (filled / on file / heard); otherwise the required set is exclusively empty
  // always-collect fields → "to collect", not a misleading green "done".
  const engaged = unit.fields.some(f => f.required && isFieldEngaged(f));
  return { pending: 0, heard, state: engaged ? 'done' : 'to-collect' };
}

/**
 * A field is required for the current scenario when the disposition engine lists it
 * (`ResolvedField.required`, derived from `required_params`) OR the schema marks it
 * base-required (`field.validation.required`). This is the SINGLE predicate that drives
 * both section visibility (`unitHasRequiredField`) and the field-level filter in
 * ScriptFlow, so the two can never disagree.
 *
 * NOTE: distinct from the schema-context `isFieldRequired(fieldId, pathPrefix)` helper in
 * `components/fields/context/SchemaContext` — that one is a curried lookup by id; this one
 * classifies an already-resolved field.
 */
export function isResolvedFieldRequired(field: ResolvedField): boolean {
  return field.required || field.field.validation?.required === true;
}

/**
 * A field is "missed" when the rep has not captured it: no value, not prefilled, not
 * marked heard. `FieldStatus` already encodes that precedence
 * (value > prefilled > heard > missing > optional), so this is the single status check
 * rather than a re-derivation — the Output board's own Missed filter keys on the same
 * `status === 'missing'`.
 */
// Not exported: the Call Script's own Missed/Required counts were removed with its
// duplicate filter (LA-7358), so this is now only used within this module.
function isResolvedFieldMissed(field: ResolvedField): boolean {
  return field.status === 'missing';
}

/** A unit is "missed-bearing" when at least one of its fields is still uncaptured. */
function unitHasMissedField(unit: ScriptUnit): boolean {
  return unit.fields.some(isResolvedFieldMissed);
}

/**
 * A unit is "required-bearing" when at least one of its fields is required for the
 * current scenario. Drives section visibility in ScriptFlow: a unit with no required
 * field is hidden until a disposition recompute makes one of its fields required.
 */
export function unitHasRequiredField(unit: ScriptUnit): boolean {
  return unit.fields.some(isResolvedFieldRequired);
}

/**
 * Whether a unit should show the blue "still needs work" accent in ScriptFlow (BI-1464).
 * True when the unit is required-bearing AND not yet done — i.e. `unitProgress().state`
 * is `pending` (a required field is missing) or `to-collect` (required always-collect
 * fields not yet engaged). A `done` unit (every required field filled/heard) and an
 * `optional` unit (no required fields) return false, so the accent falls away as the rep
 * completes a section and never marks a section that was never required.
 */
export function unitNeedsAttention(unit: ScriptUnit): boolean {
  return unitHasRequiredField(unit) && unitProgress(unit).state !== 'done';
}

/** ScriptFlow's render partition: intro/pinned units vs. the required-only flow. */
export interface PartitionedScriptUnits {
  /**
   * `always_show` (intro/pinned) units, rendered at the TOP with ALL their fields
   * intact (no required-only filter) — greeting/verification context the rep always
   * sees. Preserves the incoming `order` among themselves.
   */
  introUnits: ScriptUnit[];
  /**
   * The required-driven flow, sorted required-bearing units FIRST and non-required units
   * last (stable within each group). In the default (required-only) mode each unit is also
   * narrowed to ONLY its required fields; in `showAll` mode all fields survive and the sort
   * is what surfaces required sections above optional ones. Intro units are excluded here so
   * a unit is never rendered twice.
   */
  visibleUnits: ScriptUnit[];
}

/** The Call Script column's segmented filter (BV-2129). Mirrors the Output board's. */
export type ScriptFilterMode = 'all' | 'missed' | 'required';

/**
 * Split built units into the intro/pinned set (rendered first, unfiltered, always)
 * and the required-only flow (unchanged behavior). This is the SINGLE place the
 * intro-vs-required rule lives, so ScriptFlow stays a thin renderer and the rule is
 * unit-testable without a browser.
 *
 * - Intro units bypass BOTH filters: a `unitHasRequiredField` section check and the
 *   per-row `isResolvedFieldRequired` filter — every field shows, even when optional
 *   or already filled (FR3/FR4).
 * - Non-intro units keep the prior contract: shown only when they carry a required
 *   field, and narrowed to those required fields.
 * - Input order is preserved within each partition (FR7); `buildScriptModel` already
 *   sorts by `order`, so intro units keep their relative order at the top.
 *
 * `mode` is the column's segmented filter (BV-2129), replacing the earlier boolean
 * `showAll`:
 *
 * - `all` — every non-intro section, every field, whatever its status. The default the
 *   column opens on.
 * - `missed` — only fields still uncaptured (`status === 'missing'`), and only the
 *   sections that have one. What is left to ask on this call.
 * - `required` — only fields required for the current scenario, and only the sections
 *   that have one. The original flow, and still this function's default so every
 *   existing caller and test is untouched.
 *
 * Intro units bypass the filter in every mode — that is what `alwaysShow` means.
 *
 * Ordering (BI-1464): the flow is sorted so required-bearing units come FIRST and
 * non-required units last, stable within each group (authored `order` preserved). In
 * `required` mode every visible unit is required, so this is a no-op; it is observable
 * in `all` mode, where required sections float above the optional ones. Two filters +
 * concat (not Array.sort) guarantee stability.
 */
export function partitionScriptUnits(
  units: ScriptUnit[],
  mode: ScriptFilterMode = 'required',
): PartitionedScriptUnits {
  const introUnits = units.filter(u => u.alwaysShow);

  // One predicate pair per mode: which units survive, and which of their fields do.
  // `all` keeps everything, so it needs no field filter at all — mapping it through an
  // identity filter would allocate a new unit object per render for nothing.
  const keepUnit =
    mode === 'missed' ? unitHasMissedField : mode === 'required' ? unitHasRequiredField : null;
  const keepField =
    mode === 'missed'
      ? isResolvedFieldMissed
      : mode === 'required'
        ? isResolvedFieldRequired
        : null;

  const flowUnits = units
    .filter(u => !u.alwaysShow && (keepUnit === null || keepUnit(u)))
    .map(u => (keepField === null ? u : { ...u, fields: u.fields.filter(keepField) }));

  const visibleUnits = [
    ...flowUnits.filter(unitHasRequiredField),
    ...flowUnits.filter(u => !unitHasRequiredField(u)),
  ];
  return { introUnits, visibleUnits };
}

/** A run of a unit's fields that share a section header (undefined = ungrouped). */
export interface FieldSectionGroup {
  /** Nested-object label rendered as a sub-header; undefined for direct fields (no header). */
  section: string | undefined;
  fields: ResolvedField[];
}

/**
 * Group a unit's fields by `section` for ScriptStep's sub-headers — ONE group per distinct
 * section (first-appearance order; order within a section preserved). Merging non-consecutive
 * runs (a scalar before AND after a nested sub-object both carry the parent's label) keeps
 * section labels unique, so ScriptStep can key groups by label — no duplicate React key and
 * collapse state survives a disposition recompute. Render-only (the unit's `fields` array
 * stays flat, so required/done logic is intact).
 */
export function groupFieldsBySection(fields: ResolvedField[]): FieldSectionGroup[] {
  const groups: FieldSectionGroup[] = [];
  for (const field of fields) {
    const existing = groups.find(g => g.section === field.section);
    if (existing) {
      existing.fields.push(field);
    } else {
      groups.push({ section: field.section, fields: [field] });
    }
  }
  return groups;
}

interface BuildContext {
  outputSchemaGroups: FieldGroup[];
  outputData: Record<string, unknown>;
  inputData: Record<string, unknown>;
  missingFieldSet: Set<string>;
  /** Fields the agent heard on-recording but hasn't committed yet. */
  heardFieldSet: Set<string>;
  /** Engine-required field keys (required_params) — a superset of missing. */
  requiredFieldSet: Set<string>;
  sessionEditedKeys: Set<string>;
}

/** The shared header fields every unit derives from its question (id + display text). */
function questionMeta(question: ScriptQuestion): {
  questionId: string;
  section: string;
  prompt: string;
  helpText: string;
} {
  return {
    questionId: question.id ?? '',
    section: question.section ?? '',
    prompt: question.question ?? '',
    helpText: question.help_text ?? '',
  };
}

/** Build the single unit for a top-level (non entity-scoped) question. */
function buildTopLevelUnit(question: ScriptQuestion, ctx: BuildContext): ScriptUnit {
  const fields = resolveTopLevelFields(question, ctx);
  const { questionId, section, prompt, helpText } = questionMeta(question);
  return {
    id: questionId,
    questionId,
    section,
    prompt,
    helpText,
    entityKey: null,
    fields,
    done: unitDone(fields),
    alwaysShow: question.always_show ?? false,
  };
}

/**
 * Index input headers by `code` so a reordered output still gets the right label
 * (#4: avoid showing one drug's code over another drug's values). Elements with no
 * `code` are skipped — those pair positionally instead.
 */
function indexInputByCode(inputArray: unknown[]): Map<string, Record<string, unknown>> {
  const inputByCode = new Map<string, Record<string, unknown>>();
  for (const raw of inputArray) {
    const el = (raw ?? {}) as Record<string, unknown>;
    if (el.code != null) inputByCode.set(String(el.code), el);
  }
  return inputByCode;
}

/**
 * Derive an entity instance's header identity. Prefers the rendered output
 * element's own code/name so the label always describes the values shown beneath
 * it; the matched input element enriches it. Falls back to a 1-based position.
 */
function resolveEntityInstanceMeta(
  outElem: Record<string, unknown>,
  inElem: Record<string, unknown>,
  index: number,
): { code: string; name?: string } {
  const codeSource = outElem.code ?? inElem.code;
  const code = codeSource != null ? String(codeSource) : String(index + 1);
  const nameSource = outElem.name ?? inElem.name;
  const name = nameSource != null ? String(nameSource) : undefined;
  return { code, name };
}

/** Build one fanned-out unit for entity instance `index`. */
function buildEntityInstanceUnit(
  question: ScriptQuestion,
  entityKey: string,
  index: number,
  entityTemplate: FormField | undefined,
  outputArray: unknown[],
  inputArray: unknown[],
  inputByCode: Map<string, Record<string, unknown>>,
  ctx: BuildContext,
): ScriptUnit {
  const { questionId, section, prompt, helpText } = questionMeta(question);
  const outElem = (outputArray[index] ?? {}) as Record<string, unknown>;
  const positionalInput = (inputArray[index] ?? {}) as Record<string, unknown>;
  // Prefer the input element matched by the output's own code (handles skew);
  // otherwise pair positionally (the single-writer index-alignment invariant).
  const inElem =
    outElem.code != null && inputByCode.has(String(outElem.code))
      ? (inputByCode.get(String(outElem.code)) as Record<string, unknown>)
      : positionalInput;
  const { code, name } = resolveEntityInstanceMeta(outElem, inElem, index);
  const fields = resolveEntityFields(
    question,
    entityKey,
    index,
    entityTemplate,
    outputArray[index],
    ctx,
  );
  return {
    id: `${questionId}#${index}`,
    questionId,
    section,
    prompt,
    helpText,
    entityKey,
    instance: { index, code, name },
    fields,
    done: unitDone(fields),
    alwaysShow: question.always_show ?? false,
  };
}

/** Fan an entity-scoped question out into one unit per entity instance. */
function buildEntityUnits(
  question: ScriptQuestion,
  entityKey: string,
  ctx: BuildContext,
): ScriptUnit[] {
  const inputArrayKey = ENTITY_INPUT_KEY[entityKey] ?? entityKey;
  const inputArrayRaw = ctx.inputData[inputArrayKey];
  const inputArray = Array.isArray(inputArrayRaw) ? inputArrayRaw : [];
  const entityTemplate = findEntityField(ctx.outputSchemaGroups, entityKey);
  const outputArrayRaw = ctx.outputData[entityKey];
  const outputArray = Array.isArray(outputArrayRaw) ? outputArrayRaw : [];

  // Fan out across BOTH arrays. The output array is the source of truth for the
  // values (keys are output-indexed — `entity[i].relative` — matching the engine's
  // disposition paths), so we must render every output instance; we also render
  // input-only instances so a question still appears before the server seeds the
  // output slot. Using max(len) means neither array silently drops units (#5: an
  // entity whose input array is absent/renamed still fans out from the output).
  const count = Math.max(inputArray.length, outputArray.length);
  const inputByCode = indexInputByCode(inputArray);

  const units: ScriptUnit[] = [];
  for (let index = 0; index < count; index++) {
    units.push(
      buildEntityInstanceUnit(
        question,
        entityKey,
        index,
        entityTemplate,
        outputArray,
        inputArray,
        inputByCode,
        ctx,
      ),
    );
  }
  return units;
}

/**
 * Resolve an engine missing-field key — a top-level `field_id` or an
 * `entity[i].relative.path` — into a {@link ResolvedField} for the "Other required
 * fields" catch-all. Returns null when the key can't be located in the schema.
 */
function resolveMissingKey(key: string, ctx: BuildContext): ResolvedField | null {
  const entityMatch = /^(\w+)\[(\d+)\]\.(.+)$/.exec(key);
  if (entityMatch) {
    const [, entityKey, indexStr, relative] = entityMatch;
    const def = resolveTemplateField(findEntityField(ctx.outputSchemaGroups, entityKey), relative);
    if (!def) return null;
    const arr = ctx.outputData[entityKey];
    const element = Array.isArray(arr) ? arr[Number(indexStr)] : undefined;
    const value = readRelativePath(element, relative);
    return {
      key,
      field: def,
      value,
      status: computeStatus(
        key,
        value,
        ctx.missingFieldSet,
        ctx.heardFieldSet,
        ctx.sessionEditedKeys,
      ),
      required: true,
    };
  }
  const def = findTopLevelFieldByPath(ctx.outputSchemaGroups, key);
  if (!def) return null;
  const value = readRelativePath(ctx.outputData, key);
  return {
    key,
    field: def,
    value,
    status: computeStatus(
      key,
      value,
      ctx.missingFieldSet,
      ctx.heardFieldSet,
      ctx.sessionEditedKeys,
    ),
    required: true,
  };
}

/** The question's entity key, or null for a top-level (non entity-scoped) question. */
function questionEntityKey(question: ScriptQuestion): string | null {
  return question.entity_key && question.entity_key.length > 0 ? question.entity_key : null;
}

/**
 * "Other required fields": engine-missing keys that NO built unit already covers,
 * resolved into {@link ResolvedField}s for the catch-all section (deduped, schema
 * order preserved). Keys that don't resolve against the schema are dropped.
 */
function buildOtherFields(
  units: ScriptUnit[],
  /**
   * REQUIRED keys, not missing ones. Sourcing this from `missing_fields` made a field
   * vanish from the catch-all the moment it was filled — the engine drops it from that
   * list — so a rep who typed a value watched the row disappear under them, along with
   * its suggestion and evidence. Required keys are stable across the call, so the row
   * stays put and simply changes status (BV-1575).
   */
  requiredFields: string[],
  ctx: BuildContext,
): ResolvedField[] {
  const coveredKeys = new Set<string>();
  for (const unit of units) {
    for (const field of unit.fields) coveredKeys.add(field.key);
  }
  const otherFields: ResolvedField[] = [];
  const seenOther = new Set<string>();
  for (const key of requiredFields) {
    if (coveredKeys.has(key) || seenOther.has(key)) continue;
    const resolved = resolveMissingKey(key, ctx);
    if (resolved) {
      seenOther.add(key);
      otherFields.push(resolved);
    }
  }
  return otherFields;
}

/**
 * Build the guided-script model: ordered units (with per-instance fan-out),
 * each field resolved to its schema def + live status, plus the first unit
 * that still has a missing field, plus any required fields not covered by the
 * script (the "Other required fields" catch-all).
 */
export function buildScriptModel(args: {
  script: Script | undefined;
  outputSchemaGroups: FieldGroup[];
  outputData: Record<string, unknown>;
  inputData: Record<string, unknown>;
  missingFields: string[];
  /** Engine `required_params` (superset of missing). Optional; defaults to missing. */
  requiredFields?: string[];
  /**
   * Fields the agent heard on-recording but hasn't yet committed. Optional;
   * defaults to an empty set; the workspace hook passes the session's edited keys.
   */
  heardKeys?: Set<string>;
  sessionEditedKeys: Set<string>;
}): ScriptModel {
  const { script } = args;

  const units: ScriptUnit[] = [];
  if (!script?.questions || script.questions.length === 0) {
    return { units, currentUnitId: null, otherFields: [] };
  }

  const ctx: BuildContext = {
    outputSchemaGroups: args.outputSchemaGroups,
    outputData: args.outputData,
    inputData: args.inputData,
    missingFieldSet: new Set(args.missingFields),
    heardFieldSet: args.heardKeys ?? new Set(),
    // Fall back to the missing set when required isn't supplied, so a field that
    // is missing is always also treated as required.
    requiredFieldSet: new Set(args.requiredFields ?? args.missingFields),
    sessionEditedKeys: args.sessionEditedKeys,
  };

  for (const question of sortedQuestions(script.questions)) {
    const entityKey = questionEntityKey(question);
    if (entityKey) {
      units.push(...buildEntityUnits(question, entityKey, ctx));
    } else {
      units.push(buildTopLevelUnit(question, ctx));
    }
  }

  const otherFields = buildOtherFields(units, args.requiredFields ?? [], ctx);

  // The first unit that still has a `missing` field (heard doesn't count — it's
  // acknowledged on-recording, not blocking). Null when everything is resolved.
  const currentUnit = units.find(u => u.fields.some(f => f.status === 'missing'));

  return { units, currentUnitId: currentUnit ? currentUnit.id : null, otherFields };
}
